import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TextField,
  IconButton,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import type { UnmatchedRide } from '../../../../types/simulation';
import { unmatchedRideStableKey } from './utils';
import {
  buildUnmatchedRideSummaryColumns,
  formatUnmatchedRideCell,
  getUnmatchedRideCopyText,
  QuickCopyButton,
  unmatchedSummaryColumnSupportsQuickCopy,
  UnmatchedRideIdsIcon,
} from './UnmatchedRideDetails';

import { Icon } from '@liftango/ui-icons';
const TARMAC = '#3A3A48';
const FONT_FAMILY = '"Roboto", sans-serif';

export type { UnmatchedRide };

export interface UnmatchedRidesListProps {
  data: UnmatchedRide[] | null | undefined;
  selectedUnmatchedRideKey?: string | null;
  onSelectRide?: (ride: UnmatchedRide) => void;
}

const cellSx = {
  color: '#171a1f',
  fontSize: '0.75rem',
  borderColor: 'rgba(0,0,0,0.08)',
  maxWidth: 280,
  verticalAlign: 'top' as const,
  py: 0.85,
  px: 1,
  lineHeight: 1.35,
};

const headSx = {
  color: '#171a1f',
  fontWeight: 700,
  fontSize: '0.7rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  borderColor: 'rgba(0,0,0,0.10)',
  backgroundColor: 'rgba(42,44,56,1)',
  whiteSpace: 'nowrap' as const,
  py: 1,
  px: 1,
};

type SortDirection = 'asc' | 'desc';

function toNumericOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
}

function toDateMsOrNull(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

function totalPassengers(ride: UnmatchedRide): number | null {
  const passengers = ride.passengers;
  if (!Array.isArray(passengers) || passengers.length === 0) return null;
  const total = passengers.reduce((sum, p) => {
    if (p != null && typeof p === 'object' && typeof p.number_of_passengers === 'number') {
      return sum + p.number_of_passengers;
    }
    return sum + 1;
  }, 0);
  return total > 0 ? total : null;
}

function sortValueForColumn(
  ride: UnmatchedRide,
  columnId: string,
  t: import('i18next').TFunction<'simulation'>
): string | number | null {
  switch (columnId) {
    case 'from_lat_lon':
      return toNumericOrNull(ride.from_latitude);
    case 'to_lat_lon':
      return toNumericOrNull(ride.to_latitude);
    case 'created_at':
      return toDateMsOrNull(ride.created_at) ?? formatUnmatchedRideCell(ride, columnId, t);
    case 'passengers':
      return totalPassengers(ride) ?? formatUnmatchedRideCell(ride, columnId, t);
    default:
      return formatUnmatchedRideCell(ride, columnId, t);
  }
}

function compareSortValues(a: string | number | null, b: string | number | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
}

const UnmatchedRidesList: React.FC<UnmatchedRidesListProps> = ({
  data,
  selectedUnmatchedRideKey = null,
  onSelectRide,
}) => {
  const { t } = useTranslation('simulation');
  const summaryColumns = useMemo(() => buildUnmatchedRideSummaryColumns(t), [t]);
  const list = data ?? [];
  const hasData = data !== undefined && data !== null;
  const isEmpty = list.length === 0;
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [showFilters, setShowFilters] = useState(false);

  const filteredList = useMemo(() => {
    const activeFilters = Object.entries(filters).filter(([, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return list;
    return list.filter((ride) =>
      activeFilters.every(([columnId, needleRaw]) => {
        const needle = needleRaw.trim().toLowerCase();
        const hay = formatUnmatchedRideCell(ride, columnId, t).toLowerCase();
        return hay.includes(needle);
      })
    );
  }, [filters, list, t]);

  const sortedList = useMemo(() => {
    const sorted = [...filteredList];
    sorted.sort((a, b) => {
      const cmp = compareSortValues(sortValueForColumn(a, sortBy, t), sortValueForColumn(b, sortBy, t));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredList, sortBy, sortDirection, t]);

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((value) => value.trim() !== ''),
    [filters]
  );

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(columnId);
    setSortDirection('asc');
  };

  const clearFilters = () => setFilters({});

  return (
    <Box
      sx={{
        backgroundColor: TARMAC,
        fontFamily: FONT_FAMILY,
        height: '100%',
        minHeight: 120,
        display: 'flex',
        flexDirection: 'column',
        px: 1.5,
        py: 1.25,
      }}
    >
      {isEmpty ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            py: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '0.95rem',
              fontWeight: 600,
              color: hasData ? '#22C55E' : '#6b7280',
            }}
          >
            {hasData
              ? t('results.unmatched.noUnmatched')
              : t('results.unmatched.noData')}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#171a1f',
                flexShrink: 0,
              }}
            >
              {t('results.unmatched.headerCount', { visible: sortedList.length, total: list.length })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <IconButton
                size="small"
                onClick={() => setShowFilters((v) => !v)}
                title={showFilters ? t('results.unmatched.hideFilters') : t('results.unmatched.showFilters')}
                sx={{
                  color: showFilters || hasActiveFilters ? '#171a1f' : '#52565e',
                  border: '1px solid rgba(0,0,0,0.12)',
                  borderRadius: 1,
                }}
              >
                <Icon size={17} name="filter" />
              </IconButton>
              {hasActiveFilters && (
              <IconButton
                size="small"
                onClick={clearFilters}
                title={t('results.unmatched.clearFilters')}
                sx={{ color: '#374151' }}
              >
                <Icon size={17} name="xmark" />
              </IconButton>
              )}
            </Box>
          </Box>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              backgroundColor: 'rgba(0,0,0,0.22)',
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: 1,
            }}
          >
            <Table stickyHeader size="small" sx={{ minWidth: summaryColumns.length * 120 + 96 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      ...headSx,
                      position: 'sticky',
                      top: 0,
                      left: 0,
                      zIndex: 6,
                      minWidth: 44,
                      width: 44,
                      maxWidth: 44,
                    }}
                  >
                    {t('results.unmatched.rowNum')}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...headSx,
                      position: 'sticky',
                      top: 0,
                      left: 44,
                      zIndex: 6,
                      minWidth: 40,
                      width: 40,
                      maxWidth: 40,
                      textAlign: 'center',
                      px: 0.5,
                    }}
                    title={t('results.unmatched.identifiersColumnTitle')}
                    aria-label={t('results.unmatched.identifiersColumnAria')}
                  >
                    <Icon size={16} name="circle-info" style={{ opacity: 0.9, verticalAlign: 'middle' }} aria-hidden />
                  </TableCell>
                  {summaryColumns.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{
                        ...headSx,
                        position: 'sticky',
                        top: 0,
                        zIndex: 5,
                        ...(col.monospace
                          ? { fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }
                          : {}),
                      }}
                    >
                      <TableSortLabel
                        active={sortBy === col.id}
                        direction={sortBy === col.id ? sortDirection : 'asc'}
                        onClick={() => handleSort(col.id)}
                        sx={{
                          color: 'inherit',
                          '& .MuiTableSortLabel-icon': { color: '#374151 !important' },
                          '&:hover': { color: '#fff' },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
                {showFilters && (
                  <TableRow>
                    <TableCell
                      sx={{
                        ...headSx,
                        position: 'sticky',
                        top: 40,
                        left: 0,
                        zIndex: 6,
                        minWidth: 44,
                        width: 44,
                        maxWidth: 44,
                        py: 0.6,
                      }}
                    >
                    </TableCell>
                    <TableCell
                      sx={{
                        ...headSx,
                        position: 'sticky',
                        top: 40,
                        left: 44,
                        zIndex: 6,
                        minWidth: 40,
                        width: 40,
                        maxWidth: 40,
                        py: 0.6,
                        px: 0.5,
                      }}
                    />
                    {summaryColumns.map((col) => (
                      <TableCell
                        key={`filter-${col.id}`}
                        sx={{
                          ...headSx,
                          position: 'sticky',
                          top: 40,
                          zIndex: 5,
                          py: 0.4,
                          px: 0.7,
                          textTransform: 'none',
                          letterSpacing: 'normal',
                        }}
                      >
                        <TextField
                          size="small"
                          variant="outlined"
                          value={filters[col.id] ?? ''}
                          onChange={(e) =>
                            setFilters((prev) => ({ ...prev, [col.id]: e.target.value }))
                          }
                          placeholder={t('results.unmatched.filterPlaceholder')}
                          fullWidth
                          inputProps={{ 'aria-label': t('results.unmatched.filterAria', { label: col.label }) }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: '#171a1f',
                              fontSize: '0.72rem',
                              backgroundColor: 'rgba(0,0,0,0.025)',
                              '& fieldset': { borderColor: 'rgba(0,0,0,0.14)' },
                              '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.22)' },
                              '&.Mui-focused fieldset': { borderColor: '#6b7280' },
                            },
                            '& .MuiOutlinedInput-input': {
                              py: 0.45,
                              px: 0.75,
                            },
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {sortedList.map((ride, idx) => {
                  const rideKey = unmatchedRideStableKey(ride);
                  const isSelected =
                    selectedUnmatchedRideKey != null && selectedUnmatchedRideKey === rideKey;
                  return (
                  <TableRow
                    key={rideKey}
                    hover
                    onClick={onSelectRide ? () => onSelectRide(ride) : undefined}
                    sx={{
                      cursor: onSelectRide ? 'pointer' : 'default',
                      backgroundColor: isSelected ? 'rgba(245, 158, 11, 0.18)' : undefined,
                      '&:hover .unmatched-row-num': {
                        backgroundColor: 'rgba(0,0,0,0.04)',
                      },
                      '& .unmatched-row-num': {
                        outline: isSelected ? '2px solid rgba(245, 158, 11, 0.8)' : 'none',
                        outlineOffset: -2,
                      },
                    }}
                  >
                    <TableCell
                      className="unmatched-row-num"
                      sx={{
                        ...cellSx,
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        backgroundColor: 'rgba(45,47,58,0.97)',
                        fontWeight: 600,
                        color: '#52565e',
                        minWidth: 44,
                        width: 44,
                        maxWidth: 44,
                        borderRight: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '2px 0 6px rgba(0,0,0,0.15)',
                      }}
                    >
                      {idx + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellSx,
                        position: 'sticky',
                        left: 44,
                        zIndex: 1,
                        backgroundColor: 'rgba(45,47,58,0.97)',
                        minWidth: 40,
                        width: 40,
                        maxWidth: 40,
                        textAlign: 'center',
                        px: 0.25,
                        py: 0.5,
                        borderRight: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '2px 0 6px rgba(0,0,0,0.12)',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <UnmatchedRideIdsIcon ride={ride} surface="tableRow" />
                      </Box>
                    </TableCell>
                    {summaryColumns.map((col) => {
                      const text = formatUnmatchedRideCell(ride, col.id, t);
                      const copyText = getUnmatchedRideCopyText(ride, col.id, t);
                      const showCopy =
                        copyText != null && unmatchedSummaryColumnSupportsQuickCopy(col.id);
                      return (
                        <TableCell
                          key={col.id}
                          title={text !== '—' ? text : undefined}
                          sx={{
                            ...cellSx,
                            ...(col.monospace
                              ? {
                                  fontFamily: 'ui-monospace, "Cascadia Code", Menlo, Consolas, monospace',
                                  fontSize: '0.7rem',
                                }
                              : {}),
                            wordBreak: 'break-word',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 0.25,
                              justifyContent: 'space-between',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                wordBreak: 'break-word',
                              }}
                            >
                              {text}
                            </Box>
                            {showCopy ? (
                              <QuickCopyButton
                                textToCopy={copyText}
                                ariaLabel={t('results.unmatched.copyFieldAria', { label: col.label })}
                                tone="onDark"
                              />
                            ) : null}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )})}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default UnmatchedRidesList;
